import { useCities } from "../../../../contexts/CitiesProvider";
import type { ChoiceRow } from "../../../../lib/forms/fieldOptions";
import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  unlabeledAriaProps,
} from "../../field-ui";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";

type CityOption = { id: string; name: string };

const INITIAL_CITIES_LIMIT = 100;
const PINNED_CITY_NAMES = [
  "Москва",
  "Ростов-на-Дону",
  "Краснодар",
  "Санкт-Петербург",
] as const;

interface CityInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  validation?: (value: string) => boolean;
  /** Whitelist из city.choices — поиск только по ним. Пусто = весь справочник. */
  allowedChoices?: ChoiceRow[];
}

const CityInput = forwardRef<FormFieldRef, CityInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const {
    cities: allCities,
    isLoading: citiesLoading,
    error: citiesError,
    retry: retryCities,
  } = useCities();
  const allowedChoices = props.allowedChoices;
  const usesAllowedChoices = Boolean(
    allowedChoices && allowedChoices.length > 0,
  );
  const catalogLoading = !usesAllowedChoices && citiesLoading;
  const catalogError = !usesAllowedChoices ? citiesError : null;

  const catalog = useMemo((): CityOption[] => {
    if (allowedChoices && allowedChoices.length > 0) {
      return allowedChoices.map((row) => ({
        id: row.value,
        name: row.label,
      }));
    }
    return allCities.map((city) => ({
      id: String(city.id),
      name: city.name ?? "",
    }));
  }, [allowedChoices, allCities]);

  const initialCities = useMemo(() => {
    const pinned = PINNED_CITY_NAMES.flatMap((name) => {
      const city = catalog.find(
        (item) =>
          item.name.toLocaleLowerCase("ru-RU") ===
          name.toLocaleLowerCase("ru-RU"),
      );
      return city ? [city] : [];
    });
    const pinnedIds = new Set(pinned.map((city) => city.id));

    return [
      ...pinned,
      ...catalog.filter((city) => !pinnedIds.has(city.id)),
    ].slice(0, INITIAL_CITIES_LIMIT);
  }, [catalog]);

  const [value, setValue] = useState(props.initialValue ?? "");
  const [validationState, setValidationState] = useState(true);
  const [err, setErr] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResult, setSearchResult] = useState<CityOption[]>([]);
  const [isOpened, setOpened] = useState(false);
  const [isFocused, setFocused] = useState(false);

  useEffect(() => {
    if (!value || searchTerm) return;
    const restoredCity = catalog.find((city) => city.id === value);
    if (restoredCity) setSearchTerm(restoredCity.name);
  }, [catalog, searchTerm, value]);

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(props.fieldName, next, props.onValueChange);
  };

  const clear = () => {
    commitValue("");
    setSearchTerm("");
  };

  const validate = () => {
    if (searchTerm.trim() && catalogLoading) {
      setErr("Дождитесь загрузки списка городов");
      setValidationState(false);
      return false;
    }
    if (searchTerm.trim() && catalogError) {
      setErr("Не удалось загрузить города");
      setValidationState(false);
      return false;
    }
    if (!props.validation) return true;

    const result = props.validation(value);
    if (!result) setErr("Выберите город из списка");
    setValidationState(result);
    return result;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    validate,
    clear,
  }));

  const handleOnBlur = () => {
    setFocused(false);
    setOpened(false);
    const normalizedSearchTerm = searchTerm.trim();
    setSearchTerm(normalizedSearchTerm);
    const filtered = catalog.filter(
      (city) =>
        city.name.toLocaleLowerCase("ru-RU") ===
        normalizedSearchTerm.toLocaleLowerCase("ru-RU"),
    );
    if (filtered[0]) {
      setSearchTerm(filtered[0].name);
      commitValue(filtered[0].id);
    }
  };

  const handleSelect = (id: string, cityName: string) => {
    setSearchTerm(cityName);
    commitValue(id);
    setOpened(false);
    setValidationState(true);
    setErr("");
  };

  const handleOnFocus = () => {
    setFocused(true);
    if (searchTerm.trim() === "") {
      setSearchResult(initialCities);
    }
    setOpened(true);
  };

  useEffect(() => {
    setValidationState(true);
    const delayDebounceFn = setTimeout(() => {
      if (catalogLoading || catalogError) {
        setSearchResult([]);
        setOpened(isFocused);
        return;
      }

      const normalizedSearchTerm = searchTerm.trim();
      if (normalizedSearchTerm === "") {
        setSearchResult(initialCities);
        setOpened(isFocused);
        if (value) commitValue("");
        return;
      }

      const term = normalizedSearchTerm.toLocaleLowerCase("ru-RU");
      const filtered = catalog.filter((city) =>
        city.name.toLocaleLowerCase("ru-RU").includes(term),
      );

      setSearchResult(filtered);

      const selectedCity = value
        ? catalog.find((city) => city.id === value)
        : undefined;
      const selectedCityName = selectedCity?.name
        .trim()
        .toLocaleLowerCase("ru-RU");

      if (selectedCityName === term) {
        setOpened(false);
        return;
      }

      setOpened(isFocused);
      if (value) commitValue("");
    }, 200);

    return () => clearTimeout(delayDebounceFn);
  }, [
    catalog,
    catalogError,
    catalogLoading,
    initialCities,
    isFocused,
    searchTerm,
    value,
  ]);

  useClickOutside(containerRef, () => setOpened(false), isOpened);

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel htmlFor={listId} muted={props.labelMuted}>
        {props.placeholder}
      </FieldLabel>
      <FieldControl>
        <div ref={containerRef} className="relative h-full">
          <input
            ref={inputRef}
            id={listId}
            autoComplete="off"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            type="text"
            placeholder="Начните вводить название"
            aria-invalid={!validationState}
            {...unlabeledAriaProps(props.placeholder, props.accessibleName)}
            className={getInputClassName(validationState)}
          />

          {isOpened ? (
            <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10">
              {catalogLoading ? (
                <div
                  className="flex items-center gap-2 px-4 py-3 font-onest text-[15px] text-slate-500"
                  role="status"
                >
                  <span className="size-3.5 animate-spin rounded-full border-2 border-slate-200 border-t-brand-500" />
                  Загружаем города…
                </div>
              ) : catalogError ? (
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <span
                    className="font-onest text-[15px] text-red-600"
                    role="alert"
                  >
                    Не удалось загрузить города
                  </span>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={retryCities}
                    className="shrink-0 font-onest text-sm font-medium text-brand-700 hover:underline"
                  >
                    Повторить
                  </button>
                </div>
              ) : searchResult.length > 0 ? (
                <ul
                  className="scrollbar-thin max-h-56 overflow-y-auto p-2"
                  role="listbox"
                >
                  {searchResult.map((city) => (
                    <li key={city.id}>
                      <button
                        type="button"
                        role="option"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect(city.id, city.name)}
                        className="form-choice-item w-full rounded-lg px-3 py-2.5 text-left font-onest text-[15px] text-slate-700 transition-colors hover:bg-brand-50 hover:text-brand-700"
                      >
                        {city.name}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div
                  className="px-4 py-3 font-onest text-[15px] text-slate-500"
                  role="status"
                >
                  Город не найден
                </div>
              )}
            </div>
          ) : null}
        </div>
      </FieldControl>
      <FieldFooter>
        {err ? (
          <span className="text-red-600" role="alert">
            {err}
          </span>
        ) : !validationState ? (
          <span className="text-red-600" role="alert">
            Выберите город из списка
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

CityInput.displayName = "CityInput";

export { CityInput };
export type { CityInputProps };
